import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { type ItemMedia } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ConfirmButton } from '@/components/ConfirmButton';
import { IconAction } from '@/components/IconAction';
import { MediaViewer } from '@/components/MediaViewer';
import { StatusBadge } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import type { Session } from '@core/session/types';

import { galleryOf, type MediaScope } from './mediaScope';

/**
 * The saved files of one gallery: a grid with the main picture marked, a star to promote another,
 * a bin to remove one, and the viewer on a click. Both act at once — they are writes on files that
 * already exist, not part of any form's Save.
 *
 * Shared by `MediaDrawer` and the design page, so the two cannot disagree about what a gallery
 * offers.
 */
export function MediaGallery({
  session,
  scope,
  rows,
}: {
  session: Session;
  scope: MediaScope;
  rows: ItemMedia[];
}) {
  const [viewing, setViewing] = useState<number | null>(null);
  const { basePath, cacheKey, permission } = galleryOf(scope);

  const setPrimary = useApiMutation<ItemMedia, ItemMedia>({
    mutationFn: (row) => apiFetch<ItemMedia>(`${basePath}/${row.id}/primary`, { method: 'POST' }),
    success: 'Main picture updated.',
    invalidates: [cacheKey],
  });

  const remove = useApiMutation<void, ItemMedia>({
    mutationFn: (row) => apiFetch<void>(`${basePath}/${row.id}`, { method: 'DELETE' }),
    success: (_result, row) => `${row.fileName} removed.`,
    invalidates: [cacheKey],
  });

  return (
    <>
      <div className="grid grid-cols-3 gap-3 max-sm:grid-cols-2">
        {rows.map((row, index) => (
          <figure key={row.id} className="m-0 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setViewing(index)}
              aria-label={`View ${row.fileName}`}
              className="border-border-color relative aspect-square cursor-pointer overflow-hidden rounded border bg-light"
            >
              {row.mediaType === 'Image' ? (
                <img
                  src={row.url}
                  alt={row.caption ?? row.fileName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              ) : (
                // A still frame is not available without loading the clip, so a play glyph stands
                // in — a gallery that preloads six videos costs a shop its bandwidth before anyone
                // has asked to watch one.
                <span className="text-default flex h-full w-full items-center justify-center">
                  <i className="icon icon-play text-[22px]" />
                </span>
              )}

              {row.isPrimary && (
                <span className="absolute start-1 top-1">
                  <StatusBadge tone="brand">Main</StatusBadge>
                </span>
              )}
            </button>

            <figcaption className="flex items-center gap-1">
              <span className="text-default truncate text-[11px]" title={row.fileName}>
                {row.fileName}
              </span>

              <span className="ms-auto flex items-center gap-0.5">
                {row.mediaType === 'Image' && !row.isPrimary && (
                  <WriteAction session={session} required={permission}>
                    {(disabled) => (
                      <IconAction
                        icon="icon icon-star"
                        label={`Make ${row.fileName} the main picture`}
                        disabled={disabled || setPrimary.isPending}
                        onClick={() => void setPrimary.mutateAsync(row).catch(() => undefined)}
                      />
                    )}
                  </WriteAction>
                )}

                <WriteAction session={session} required={permission}>
                  {(disabled) => (
                    <ConfirmButton
                      icon="icon icon-trash-2"
                      label={`Remove ${row.fileName}`}
                      title="Remove this file?"
                      description="The file is deleted from storage as well as from this gallery."
                      confirmText="Remove"
                      disabled={disabled}
                      pending={remove.isPending}
                      onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
                    />
                  )}
                </WriteAction>
              </span>
            </figcaption>
          </figure>
        ))}
      </div>

      {viewing !== null && rows.length > 0 && (
        <MediaViewer
          items={rows.map((row) => ({
            id: row.id,
            url: row.url,
            mediaType: row.mediaType,
            fileName: row.fileName,
            caption: row.caption,
            note: row.isPrimary ? 'Main picture' : null,
          }))}
          startAt={viewing}
          title={scope.name}
          onClose={() => setViewing(null)}
        />
      )}
    </>
  );
}

/** One file's upload, as a name, a percentage and a bar. */
export function ProgressRow({ name, fraction }: { name: string; fraction: number }) {
  const percent = Math.round(fraction * 100);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between text-[12px]">
        <span className="truncate">{name}</span>
        <span className="font-mono-nums text-default">{percent}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded bg-light">
        <div
          className="bg-primary h-full transition-[width] duration-150"
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
}
